import { REGEXP_HYPHENATE, REGEXP_SPACES, REGEXP_SUFFIX } from './constants';

export function hyphenate(value: string) {
  return value.replace(REGEXP_HYPHENATE, '$1-$2').toLowerCase();
}

export function hasClass(element: HTMLElement, value: string) {
  if (!element || !value) {
    return false;
  }

  return element.classList
    ? element.classList.contains(value)
    : element.className.indexOf(value) > -1;
}

export function setData(name: string, data: string, element?: HTMLElement | Element | null) {
  if (!element || !name || !data) return;
  // @ts-ignore
  if (element.dataset) {
    // @ts-ignore
    element.dataset[name] = data;
  } else {
    element.setAttribute(`data-${hyphenate(name)}`, data);
  }
}

export function getPointersCenter(pointers: { startX: number; startY: number }[]) {
  let pageX = 0;
  let pageY = 0;
  let count = 0;

  pointers.forEach(({ startX, startY }) => {
    pageX += startX;
    pageY += startY;
    count += 1;
  });

  pageX /= count;
  pageY /= count;

  return {
    pageX,
    pageY
  };
}

export function addClass(element: HTMLElement, value: string) {
  if (!element || !value) {
    return;
  }

  if (element.classList) {
    element.classList.add(value);
    return;
  }

  const className = element.className.trim();

  if (!className) {
    element.className = value;
  } else if (className.indexOf(value) < 0) {
    element.className = `${className} ${value}`;
  }
}

export function addListener(
  element: HTMLElement,
  type: string,
  listener: () => void,
  options: { once: boolean }
) {
  let handler = listener;

  type
    .trim()
    .split(REGEXP_SPACES)
    .forEach(event => {
      element.addEventListener(event, handler, options);
    });
}

export function removeListener(
  element: HTMLElement,
  type: string,
  listener: () => void,
  options = {}
) {
  let handler = listener;

  type
    .trim()
    .split(REGEXP_SPACES)
    .forEach(event => {
      element?.removeEventListener(event, handler, options);
    });
}

export function dispatchEvent(element: HTMLElement, type: string, data: object, options = {}) {
  let event;
  if (typeof Event === 'function' && typeof CustomEvent === 'function') {
    event = new CustomEvent(type, {
      bubbles: true,
      cancelable: true,
      detail: data,
      ...options
    });
  } else {
    event = document.createEvent('CustomEvent');
    event.initCustomEvent(type, true, true, data);
  }

  return element.dispatchEvent(event);
}

export function setStyle(element: HTMLElement, styles: object) {
  const { style } = element;

  forEach(styles, (value: number | string, property: string) => {
    if (REGEXP_SUFFIX.test(property) && isNumber(value)) {
      value += 'px';
    }

    style[property] = value;
  });
}

export function forEach(data: any, callback: any) {
  if (data && typeof callback === 'function') {
    if (Array.isArray(data) || typeof data.length === 'number') {
      const { length } = data;
      let i;

      for (i = 0; i < length; i += 1) {
        if (callback.call(data, data[i], i, data) === false) {
          break;
        }
      }
    } else if (typeof data === 'object' && data !== null) {
      Object.keys(data).forEach(key => {
        callback.call(data, data[key], key, data);
      });
    }
  }

  return data;
}

export function isNumber(data: any) {
  return typeof data === 'number';
}

export function getTransforms({
  rotate,
  scaleX,
  scaleY,
  translateX,
  translateY
}: {
  rotate: number;
  scaleX: number;
  scaleY: number;
  translateX: number;
  translateY: number;
}) {
  const values = [];

  if (isNumber(translateX) && translateX !== 0) {
    values.push(`translateX(${translateX}px)`);
  }

  if (isNumber(translateY) && translateY !== 0) {
    values.push(`translateY(${translateY}px)`);
  }

  if (isNumber(rotate) && rotate !== 0) {
    values.push(`rotate(${rotate}deg)`);
  }

  if (isNumber(scaleX) && scaleX !== 1) {
    values.push(`scaleX(${scaleX})`);
  }

  if (isNumber(scaleY) && scaleY !== 1) {
    values.push(`scaleY(${scaleY})`);
  }

  const transform = values.length ? values.join(' ') : 'none';

  return {
    WebkitTransform: transform,
    msTransform: transform,
    transform
  };
}

export function isSameFloat(n1: number, n2: number) {
  return n1.toFixed(3) === n2.toFixed(3);
}

export function removeClass(element: HTMLElement, value: string) {
  if (!element || !value) {
    return;
  }

  if (element.classList) {
    element.classList.remove(value);
    return;
  }

  if (element.className.indexOf(value) >= 0) {
    element.className = element.className.replace(value, '');
  }
}

export function toggleClass(element: HTMLElement, value: string, added: boolean) {
  if (!value) {
    return;
  }

  if (added) {
    addClass(element, value);
  } else {
    removeClass(element, value);
  }
}

export function getOffset(element: Element) {
  const box = element.getBoundingClientRect();

  return {
    left: box.left + (window.pageXOffset - document.documentElement.clientLeft),
    top: box.top + (window.pageYOffset - document.documentElement.clientTop)
  };
}
